import { aqlEscape } from '../escape-utils'

import { Tool } from './base'
import {
  makeRenderer,
  InlineGroups,
  RenderContext,
  normalizeList,
  scalarToString,
  RenderOut,
} from './render/render-kit'
import { defaultDisplayFields } from './render/rtd/datadisplay.defaults'
import { toCamelCase } from './render/rtd/datadisplay.template-renderer'
import { DataDisplayTemplate } from './render/rtd/datadisplay.types'
import { wrapRendererWithDataDisplay } from './render/rtd/datadisplay.utils'
import { NormalizedDataSource } from './render/rtd/datasource.types'
import { wrapRendererWithDatasource } from './render/rtd/datasource.utils'
import {
  qradarEventCodeByLogCategory,
  qradarMap,
} from './render/rtd/tool-datasource-mappings/qradar.datasource'
import { AstPredicate, ValueOrList } from './render/types'

/**
 * Build a QRadar SELECT field: `"{{!=ccm_field!}}" AS camelCaseAlias`.
 * The Eta wrapper lets matchboi's second-pass render substitute the
 * customer-specific extraction property name (e.g. "Target File Directory")
 * while the alias (e.g. `targetPath`) stays stable so the WHERE clause — which
 * references the tool-mapped camelCase field — resolves to the same column.
 *
 * Without the `AS` alias, QRadar treats the SELECT entry as a literal column
 * name, which breaks WHERE clauses that filter on extracted properties.
 */
function qradarSelectField(mappedField: string, ccmField: string): string {
  return `"{{!=${ccmField}!}}" AS ${mappedField}`
}

export const qradarDataDisplayTemplate: DataDisplayTemplate = {
  projection: (fields, ccmFields) =>
    fields.map((f, i) => qradarSelectField(f, ccmFields?.[i] ?? f)).join(', '),

  groupBy: (groupFields, aggClauses, ccmGroupFields) => {
    const selectParts = [
      ...groupFields.map((f, i) =>
        qradarSelectField(f, ccmGroupFields?.[i] ?? f),
      ),
      ...aggClauses,
    ]
    return `${selectParts.join(', ')}\nGROUP BY ${groupFields.join(', ')}`
  },

  timeBucket: null,

  aggFns: {
    count: (_field, alias) => `COUNT(*) AS ${alias}`,
    dcount: (field, alias) => `COUNT(DISTINCT "${field}") AS ${alias}`,
    sum: (field, alias) => `SUM("${field}") AS ${alias}`,
    avg: (field, alias) => `AVG("${field}") AS ${alias}`,
    min: (field, alias) => `MIN("${field}") AS ${alias}`,
    max: (field, alias) => `MAX("${field}") AS ${alias}`,
  },

  fieldAlias: (field, alias) => `"${field}" AS ${alias}`,

  join: (body, ddClause) => {
    if (!ddClause) return body
    // Replace the entire first-line SELECT clause (either `SELECT *` or a
    // datasource-populated column list) with the DataDisplay projection.
    const selectLine = /^SELECT [^\n]+/m
    let result = body
    const gbIdx = ddClause.indexOf('\nGROUP BY')
    if (gbIdx !== -1) {
      const selectPart = ddClause.slice(0, gbIdx)
      const groupByClause = ddClause.slice(gbIdx + 1)
      result = result.replace(selectLine, `SELECT ${selectPart}`)
      result = result.replace(/(\nLAST\s+\d+)/i, `\n${groupByClause}$1`)
      if (!result.includes(groupByClause))
        result = `${result}\n${groupByClause}`
    } else if (!ddClause.trimStart().toUpperCase().startsWith('GROUP BY')) {
      result = result.replace(selectLine, `SELECT ${ddClause}`)
    } else {
      result = result.replace(/(\nLAST\s+\d+)/i, `\n${ddClause}$1`)
      if (!result.includes(ddClause)) result = `${result}\n${ddClause}`
    }
    return result
  },
}

export { qradarMap }

export class QRadarTool extends Tool {
  public readonly toolName = 'QRadar Query'
  public readonly toolKind = 'siem' as const
  public override readonly customMapping = true

  public override supportsRtd = (): boolean => false

  public override supportedLogCategories(): string[] {
    return ['endpoint:wineventlog', 'endpoint:sysmon']
  }

  public readonly toolDefaultCCMFieldMappings = {
    //action: '', // No logging contains this value and is normalized by SIEM -- QRadar maps multiple event names based on success
    //dns_query: 'NETWORK',
    //domain: 'AccountDomain', //Field extraction existed but lacked proper regex -- Custom extraction
    //logon_process: 'Logon Process', //Non-default -- Custom extraction
    //object_loaded: 'Object Loaded', //Non-default -- Custom extraction
    //parent_process_cmdline: 'Parent Process CommandLine', //Non-default -- Custom extraction
    //registry_new_value_name: 'Registry New Value', //Non-default -- Missing logs to extract, but can predict name (4657)
    //registry_old_value_name: 'Registry Old Value', //Non-default -- Missing logs to extract, but can predict name (4657)
    //request_method: 'NETWORK',
    //service_file_name: 'Service File Name', //Non-default -- Custom extraction
    //status_code: 'NETWORK',
    //task_category: 'Event Category', //Normalized by QRadar -- may not match out logic
    //uri: 'NETWORK',
    //url: 'NETWORK',
    //user_agent: 'NETWORK',
    target_path: 'Target File Directory',
    application_name: 'Application Name',
    accesses: 'Accesses',
    dest_ip: 'Destination IP',
    dest_port: 'Destination Port',
    event_id: 'EventID',
    file_name: 'FileName',
    file_path: 'File Path',
    hostname: 'Machine ID',
    logon_type: 'Logon Type',
    message: 'Message',
    object_name: 'ObjectName',
    parent_process_path: 'Parent Process Path',
    process_cmdline: 'Process CommandLine',
    process_name: 'Process Name',
    process_path: 'Process Path',
    registry_key_name: 'Registry Value Name',
    registry_path: 'Registry Key',
    registry_value_name: 'Registry Value Data',
    service_name: 'Service Name',
    src_ip: 'Source IP',
    username: 'Username',
  }

  public generateSearchPermalink(
    rootURL: string,
    renderedContent: string,
  ): string {
    const qradarResponseSearchURI =
      'console/do/ariel/arielSearch?appName=EventViewer&pageId=EventList&dispatch=performSearch&value(searchMode)=AQL&searchOrigin=SEARCH_RESULTS_AQL&value(timeRangeType)=aqlTime&value(interval)=300000&value(searchName)=&value(searchId)=null&value(aql)='
    const encodedRenderedContent = encodeURIComponent(renderedContent)
    const encodedRenderedContentStringified = encodeURIComponent(
      `[${JSON.stringify(renderedContent)}]`,
    )
    return `${rootURL}${qradarResponseSearchURI}${encodedRenderedContent}&value(aqlLines)=${encodedRenderedContentStringified}`
  }

  public pivotEscape(key: string, value: string): string {
    return aqlEscape(value)
  }

  /**
   * QRadar AQL (WHERE-clause) renderer
   *
   * Fields
   * - Map via toolTuningFields["qradar"]; wrap with Eta: `{{!=<mapped>!}}`.
   *
   * Values & Lists
   * - Strings single-quoted (SQL style); escapes: backslash → "\\", single-quote → "''".
   * - Numbers are bare (not quoted).
   *
   * Logic & Grouping
   * - InlineGroups: **no root parentheses**; nested groups parenthesized.
   * - Glue: ` AND ` / ` OR ` (uppercase).
   *
   * Operators (single-switch style)
   * - eq / neq:
   *     single:  `F = 'v'` / `F != 'v'`
   *     multi:   `F IN ('v1','v2')` / `NOT (F IN ('v1','v2'))`
   * - contains / startswith / endswith (case-insensitive): `ILIKE` with `%` wildcards
   *     contains:       `F ILIKE '%v%'`
   *     doesnotcontain: `NOT (F ILIKE '%v%')`
   *     startswith:     `F ILIKE 'v%'`
   *     endswith:       `F ILIKE '%v'`
   *     Multi: OR-group; negative = NOT(group)
   * - Comparisons: `<`, `<=`, `>`, `>=` (numbers bare; strings quoted)
   * - Null / Empty:
   *     isnull → `F IS NULL`, isnotnull → `F IS NOT NULL`
   *     isempty → `F = ''`,   isnotempty → `F != ''`
   */

  public renderTuning = makeRenderer({
    tool: this.toolName,
    groupStrategy: InlineGroups,
    renderContext: {
      wrapField: (f) => f,
      andGlue: ' AND ',
      orGlue: ' OR ',
      // single-quoted strings with escaping
      quote: (s: string) =>
        `'${String(s).replace(/\\/g, '\\\\').replace(/'/g, "''")}'`,
    },

    formatPredicate: (
      p: AstPredicate,
      renderContext: RenderContext,
    ): string => {
      const F = renderContext.wrapField(renderContext.mapField(p.field))

      // Standardized inputs
      const rawVals: (ValueOrList | undefined)[] =
        p.value == null
          ? [undefined]
          : Array.isArray(p.value)
            ? p.value
            : [p.value]
      const vals = normalizeList(p.value).map(scalarToString)
      const isMulti = vals.length > 1

      // Type-aware quoting: numbers bare, else single-quoted
      const Q = (i = 0) =>
        typeof rawVals[i] === 'number'
          ? String(rawVals[i])
          : renderContext.quote(vals[i])

      // Helpers (consistent naming)
      const OR = (parts: string[]) => `(${parts.join(renderContext.orGlue)})`
      const NOT = (clause: string) => `NOT (${clause})`

      // Wildcard shaping for ILIKE
      const shape = (op: AstPredicate['op'], s: string) => {
        switch (op) {
          case 'contains':
            return `%${s}%`
          case 'doesnotcontain':
            return `%${s}%`
          case 'startswith':
            return `${s}%`
          case 'endswith':
            return `%${s}`
          default:
            return s
        }
      }

      switch (p.op) {
        /* null / empty */
        case 'isnull':
          return `${F} IS NULL`
        case 'isnotnull':
          return `${F} IS NOT NULL`
        case 'isempty':
          return `${F} = ''`
        case 'isnotempty':
          return `${F} != ''`

        /* equality / inequality -- additional formatting required so if is utilized*/
        case 'eq':
          if (isMulti) {
            const items = vals.map((_, i) => Q(i))
            return `${F} IN (${items.join(', ')})`
          }
          return `${F} = ${Q(0)}`

        case 'neq':
          if (isMulti) {
            const items = vals.map((_, i) => Q(i))
            return NOT(`${F} IN (${items.join(', ')})`)
          }
          return `${F} != ${Q(0)}`

        /* positive ILIKE  -- additional formatting required so if is utilized */
        case 'contains':
        case 'startswith':
        case 'endswith': {
          if (isMulti) {
            const parts = vals.map(
              (v) => `${F} ILIKE ${renderContext.quote(shape(p.op, v))}`,
            )
            return OR(parts)
          }
          return `${F} ILIKE ${renderContext.quote(shape(p.op, vals[0]))}`
        }

        /* negative ILIKE -- additional formatting required so if is utilized */
        case 'doesnotcontain': {
          if (isMulti) {
            const parts = vals.map(
              (v) => `${F} ILIKE ${renderContext.quote(shape(p.op, v))}`,
            )
            return NOT(OR(parts))
          }
          return NOT(`${F} ILIKE ${renderContext.quote(shape(p.op, vals[0]))}`)
        }

        /* comparisons */
        case 'lt':
          return `${F} < ${Q(0)}`
        case 'lte':
          return `${F} <= ${Q(0)}`
        case 'gt':
          return `${F} > ${Q(0)}`
        case 'gte':
          return `${F} >= ${Q(0)}`

        /* fallback → equality */
        default:
          if (isMulti) {
            const items = vals.map((_, i) => Q(i))
            return `${F} IN (${items.join(', ')})`
          }
          return `${F} = ${Q(0)}`
      }
    },
    // Fix to always prefix the snippet with AND
    finalize: (out: RenderOut, renderContext: RenderContext): string => {
      const base =
        out.kind === 'text' ? out.text : out.lines.join(renderContext.andGlue)

      if (!base) return ''
      return `${renderContext.andGlue}${base}`
    },
  })

  public readonly tuningText =
    'Append tuning logic to the end of the hunt logic in the WHERE clause. In some cases the hunt logic will need to be encapsulated in parenthesis, when tuning logic does not work as desired.'

  public override readonly datasourceMap = qradarMap
  public readonly dataDisplayTemplate = qradarDataDisplayTemplate

  /**
   * Wrap the QRadar WHERE-clause body in a full AQL SELECT...FROM...WHERE...LAST 7 DAYS query.
   * The base renderer's finalize() prepends " AND " — this wrapper strips it for a clean WHERE clause.
   *
   * EventCode priority: logCategory override (qradarEventCodeByLogCategory) >
   * ds.eventCodes (from datasource map).
   *
   * SELECT priority: ds.selectFields > DataDisplay default detail fields for the
   * hunt category > '*'. Populating from DataDisplay defaults ensures RTD output
   * always projects named columns even when no DataDisplay directive is set.
   */
  public wrapQRadarDatasource(
    body: string,
    ds: NormalizedDataSource | undefined,
  ): string {
    const whereBody = body.replace(/^\s+AND\s+/i, '').trim()
    if (!whereBody) return body

    const fromTarget = ds?.repo ?? 'events'

    const eventCodes = this.resolveQRadarEventCodes(ds)
    const eventCodeClause = eventCodes.length
      ? eventCodes.length === 1
        ? `eventId = '${eventCodes[0]}'`
        : `eventId IN (${eventCodes.map((c) => `'${c}'`).join(', ')})`
      : ''

    const conditions = [eventCodeClause, whereBody].filter(Boolean)
    const whereClause = conditions.join(' AND ')

    const selectFields = ds?.selectFields?.length
      ? ds.selectFields
      : this.deriveSelectFieldsFromDataDisplay(ds)

    const selectClause = selectFields.length
      ? selectFields.map((f) => `"{{!=${f}!}}" AS ${toCamelCase(f)}`).join(', ')
      : '*'

    return `SELECT ${selectClause}\nFROM ${fromTarget}\nWHERE ${whereClause}\nLAST 7 DAYS`
  }

  /**
   * Resolve EventCodes with logCategory precedence. When the AST carries
   * `logCategory` (set by renderSiemGrouped), override the datasource map's
   * static eventCodes with the log-source-scoped list — so Sysmon indices
   * emit EventCode=1 instead of 4688 for process_creation, etc.
   */
  private resolveQRadarEventCodes(
    ds: NormalizedDataSource | undefined,
  ): string[] {
    if (!ds) return []
    const logCat = ds.logCategory
    if (logCat) {
      const cat = ds.categories?.[0]
      const override = cat
        ? qradarEventCodeByLogCategory[logCat]?.[cat]
        : undefined
      if (override) return override
    }
    return ds.eventCodes ?? []
  }

  /**
   * Build SELECT fields from datadisplay.defaults when the datasource doesn't
   * provide selectFields. Uses detail-view fields, with identity fields (e.g.
   * hostname) first, matching project-mode ordering in resolveDataDisplay.
   */
  private deriveSelectFieldsFromDataDisplay(
    ds: NormalizedDataSource | undefined,
  ): string[] {
    const category = ds?.categories?.[0]
    if (!category) return []
    const entry = defaultDisplayFields[category]
    if (!entry) return []
    const identity = entry.identityFields ?? []
    const detail = entry.detail ?? []
    const seen = new Set<string>()
    const out: string[] = []
    for (const f of [...identity, ...detail]) {
      if (!seen.has(f)) {
        seen.add(f)
        out.push(f)
      }
    }
    return out
  }

  /** Emit full AQL query wrapping the WHERE body + DataDisplay clause. */
  public readonly renderQRadar = wrapRendererWithDataDisplay(
    wrapRendererWithDatasource(
      this.renderTuning,
      this.toolName,
      null,
      null,
      this.tuningText,
      (body, ds) => this.wrapQRadarDatasource(body, ds),
    ),
    this.toolName,
    qradarDataDisplayTemplate,
  )
}
